import {merge} from 'lodash'
import {delonghiSemantics, kenwoodSemantics, nutribulletSemantics} from './import/semantics'
import {
    Borders,
    Colors,
    IconsSize,
    Radius,
    Shadows,
    Spacing,
    Semantics,
    SemanticsObject,
    FontWeights,
} from './types/semantics.types'

const semanticsFromBrand = (brand: string): Semantics => {
    let {
        colors,
        borders,
        radius,
        shadows,
        spacing,
        iconsSize,
        fontWeights,
        semanticOverrides,
    }: Semantics = {
        colors: {} as Colors,
        borders: {} as Borders,
        radius: {} as Radius,
        shadows: {} as Shadows,
        spacing: {} as Spacing,
        iconsSize: {} as IconsSize,
        fontWeights: {} as FontWeights,
        semanticOverrides: {},
    }

    switch (brand) {
        case 'kenwood':
            colors = kenwoodSemantics.colors
            borders = kenwoodSemantics.borders
            radius = kenwoodSemantics.radius
            iconsSize = kenwoodSemantics.iconsSize
            spacing = kenwoodSemantics.spacing
            shadows = kenwoodSemantics.shadows
            fontWeights = kenwoodSemantics.fontWeights
            semanticOverrides = kenwoodSemantics.semanticOverrides
            break
        case 'nutribullet':
            colors = nutribulletSemantics.colors
            borders = nutribulletSemantics.borders
            radius = nutribulletSemantics.radius
            iconsSize = nutribulletSemantics.iconsSize
            shadows = nutribulletSemantics.shadows
            spacing = nutribulletSemantics.spacing
            fontWeights = nutribulletSemantics.fontWeights
            semanticOverrides = nutribulletSemantics.semanticOverrides
            break
        case 'delonghi':
        default:
            colors = delonghiSemantics.colors
            borders = delonghiSemantics.borders
            radius = delonghiSemantics.radius
            iconsSize = delonghiSemantics.iconsSize
            shadows = delonghiSemantics.shadows
            spacing = delonghiSemantics.spacing
            fontWeights = delonghiSemantics.fontWeights
            semanticOverrides = {}
            break
    }
    return {colors, borders, radius, spacing, iconsSize, shadows, fontWeights, semanticOverrides}
}

const getSemantics = (brand: string): SemanticsObject => {
    const {colors, borders, radius, shadows, spacing, iconsSize, semanticOverrides, fontWeights} =
        semanticsFromBrand(brand)

    const standarSemantics = {
        colors: {
            text: {
                default: colors.black[100],
                primary: colors['brand-secondary'][100],
                secondary: colors.black[60],
                tertiary: colors['brand-primary'][100],
                inverse: colors.white,
                disable: colors.black[50],
                success: colors.success[100],
                danger: colors.danger[100],
                violet: colors['brand-tertiary'][100],
            },
            border: {
                light: colors.borderLight,
                dark: colors.black[100],
                danger: colors.danger[100],
                table: colors.black[20],
                secondary: colors.black[60],
                disabled: colors.black[30],
                success: colors.success[100],
            },
            background: {
                default: colors.white,
                dark: colors.black[100],
                'light-dark': colors.black[10],
                brand: colors['brand-secondary'][100],
                alternative: colors['brand-primary'][100],
                light: colors.black[10],
                grey: colors.black[20],
                'light-grey': colors.black[20],
                'light-blue': colors['brand-tertiary'][100],
                info: colors['brand-tertiary'][60],
                danger: colors.danger[60],
                success: colors.success[60],
                'overlay-modal': colors.overlayBackgroundModal,
                image: colors.imageBackground,
            },
            button: {
                text: {
                    'primary-default': colors.white,
                    'primary-hover': colors.white,
                    'primary-focused': colors.white,
                    'primary-disabled': colors.black[50],
                    'primary-pressed': colors.white,
                    'secondary-default': colors['brand-secondary'][100],
                    'secondary-hover': colors.white,
                    'secondary-focused': colors.white,
                    'secondary-disabled': colors.black[50],
                    'secondary-pressed': colors.success[100],
                    'icon-default': colors['brand-secondary'][100],
                    'icon-hover': colors.black[50],
                    'icon-disabled': colors.black[30],
                    'icon-pressed': colors.success[100],
                    'text-default': colors['brand-primary'][100],
                    'text-hover': colors['brand-primary'][80], //same as primary-hover background
                    // inverse
                    'inverse-primary-default': colors.black[100],
                    'inverse-primary-hover': colors.black[100],
                    'inverse-primary-focused': colors.black[100],
                    'inverse-primary-disabled': colors.black[60],
                    'inverse-primary-pressed': colors.white,
                    'inverse-secondary-default': colors.black[100],
                    'inverse-secondary-hover': colors.black[100],
                    'inverse-secondary-focused': colors.black[100],
                    'inverse-secondary-disabled': colors.black[50],
                    'inverse-secondary-pressed': colors.success[100],
                    'inverse-icon-default': colors.white,
                    'inverse-icon-hover': colors.black[30],
                    'inverse-icon-disabled': colors.black[60],
                    'inverse-icon-pressed': colors.success[100],
                    'inverse-text-default': colors.white,
                    'inverse-text-hover': colors.black[30],
                },
                background: {
                    'primary-default': colors['brand-primary'][100],
                    'primary-hover': colors['brand-primary'][80],
                    'primary-focused': colors['brand-primary'][100],
                    'primary-disabled': colors.black[20],
                    'primary-pressed': colors.success[100],
                    'secondary-default': colors.null,
                    'secondary-hover': colors.black[70],
                    'secondary-focused': colors['brand-primary'][100],
                    'secondary-disabled': colors.null,
                    'secondary-pressed': colors.null,
                    'icon-default': colors.null,
                    'icon.hover': colors.null,
                    'icon.disabled': colors.null,
                    'text-default': colors.null,
                    'text-hover': colors.null,
                    // inverse
                    'inverse-primary-default': colors.white,
                    'inverse-primary-hover': colors.black[30],
                    'inverse-primary-focused': colors.white,
                    'inverse-primary-disabled': colors.black[30],
                    'inverse-primary-pressed': colors.success[100],
                    'inverse-secondary-default': colors.white,
                    'inverse-secondary-hover': colors.black[30],
                    'inverse-secondary-focused': colors.white,
                    'inverse-secondary-disabled': colors.null,
                    'inverse-secondary-pressed': colors.null,
                    'inverse-icon-default': colors.null,
                    'inverse-icon-hover': colors.null,
                    'inverse-icon-disabled': colors.null,
                    'inverse-text-default': colors.null,
                    'inverse-text-hover': colors.null,
                },
                border: {
                    'primary-default': colors.null,
                    'primary-hover': colors.null,
                    'primary-focused': colors['brand-secondary'][100],
                    'primary-disabled': colors.null,
                    'secondary-default': colors['brand-secondary'][100],
                    'secondary-hover': colors.null,
                    'secondary-focused': colors['brand-secondary'][100],
                    'secondary-disabled': colors.black[50],
                    'secondary-pressed': colors.success[100],
                    'icon-default': colors.null,
                    'icon-hover': colors.null,
                    'icon-disabled': colors.null,
                    'text-default': colors.null,
                    'text-hover': colors.null,
                    // inverse
                    'inverse-primary-default': colors.null,
                    'inverse-primary-hover': colors.null,
                    'inverse-primary-focused': colors.black[40],
                    'inverse-primary-disabled': colors.null,
                    'inverse-secondary-default': colors.white,
                    'inverse-secondary-hover': colors.null,
                    'inverse-secondary-focused': colors.black[40],
                    'inverse-secondary-disabled': colors.black[60],
                    'inverse-secondary-pressed': colors.white,
                    'inverse-icon-default': colors.null,
                    'inverse-icon-hover': colors.null,
                    'inverse-icon-disabled': colors.null,
                    'inverse-text-default': colors.null,
                    'inverse-text-hover': colors.null,
                },
            },
            label: {
                text: {
                    primary: colors.white,
                    secondary: colors['brand-secondary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[60],
                },
                background: {
                    primary: colors['brand-secondary'][100],
                    secondary: colors['brand-tertiary'][100],
                    success: colors.success[60],
                    danger: colors.danger[60],
                    disable: colors.black[20],
                },
            },
            icons: {
                text: {
                    primary: colors.white,
                    secondary: colors.black[100],
                    tertiary: colors['brand-primary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[60],
                },
                background: {
                    primary: colors.white,
                    secondary: colors.black[100],
                    tertiary: colors['brand-primary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[20],
                },
            },
        },
        space: {
            xxsmall: spacing.xxsmall,
            xsmall: spacing.xsmall,
            small: spacing.small,
            medium: spacing.medium,
            large: spacing.large,
            xlarge: spacing.xlarge,
        },
        borders: {
            null: borders.null,
            small: borders.small,
            large: borders.large,
            button: {
                border: {
                    'primary-default': 'none',
                    'primary-hover': 'none',
                    'primary-focused': borders.large,
                    'primary-disabled': 'none',
                    'secondary-default': borders.small,
                    'secondary-hover': 'none',
                    'secondary-focused': borders.large,
                    'secondary-disabled': borders.small,
                    'icon-default': 'none',
                    'icon-hover': 'none',
                    'icon-disabled': 'none',
                    'text-default': 'none',
                    'text-hover': 'none',
                    //inverse
                    'inverse-primary-default': 'none',
                    'inverse-primary-hover': 'none',
                    'inverse-primary-focused': borders.large,
                    'inverse-primary-disabled': 'none',
                    'inverse-secondary-default': borders.small,
                    'inverse-secondary-hover': 'none',
                    'inverse-secondary-focused': borders.large,
                    'inverse-secondary-disabled': borders.small,
                    'inverse-icon-default': 'none',
                    'inverse-icon-hover': 'none',
                    'inverse-icon-disabled': 'none',
                    'inverse-text-default': 'none',
                    'inverse-text-hover': 'none',
                },
            },
        },
        radii: {
            button: {
                radius: {
                    'primary-default': radius.button,
                    'primary-hover': radius.button,
                    'primary-focused': radius.button,
                    'primary-disabled': radius.button,
                    'secondary-default': radius.button,
                    'secondary-hover': radius.button,
                    'secondary-focused': radius.button,
                    'secondary-disabled': radius.button,
                    'icon-default': radius.null,
                    'icon-hover': radius.null,
                    'icon-disabled': radius.null,
                    'text-default': radius.null,
                    'text-hover': radius.null,
                    //inverse
                    'inverse-primary-default': radius.button,
                    'inverse-primary-hover': radius.button,
                    'inverse-primary-focused': radius.button,
                    'inverse-primary-disabled': radius.button,
                    'inverse-secondary-default': radius.button,
                    'inverse-secondary-hover': radius.button,
                    'inverse-secondary-focused': radius.button,
                    'inverse-secondary-disabled': radius.button,
                    'inverse-icon-default': radius.null,
                    'inverse-icon-hover': radius.null,
                    'inverse-icon-disabled': radius.null,
                    'inverse-text-default': radius.null,
                    'inverse-text-hover': radius.null,
                },
            },
            null: radius.null,
            small: radius.small,
            medium: radius.medium,
            large: radius.large,
        },
        sizes: {
            iconsSize: {
                small: iconsSize.small,
                medium: iconsSize.medium,
                large: iconsSize.large,
                xlarge: iconsSize.xlarge,
            },
        },
        shadows: {
            borderShadow: shadows.borderShadow,
            borderShadowBottom: shadows.borderShadowBottom,
        },
        fontWeights: {
            normal: fontWeights.normal,
            medium: fontWeights.medium,
            bold: fontWeights.bold,
        },
    }

    return merge({}, standarSemantics, semanticOverrides)
}

export default getSemantics
